import 'document-register-element/build/document-register-element';

import './main.scss';

import internationalPhone from '@dwse-forms/commons/lib/util/validator/internationalPhone';
import kboNummer from '@dwse-forms/commons/lib/util/validator/kbo-nummer';
import { validateInternationalPhoneNumber } from '@dwse/util/js/international-phone-validator';

import Vue from 'vue';
import VueI18n from 'vue-i18n';
import VueCustomElement from 'vue-custom-element';
import VlUiVueComponents, {
  VlLink,
  VlButton,
  VlIcon,
  VlIconWrapper,
  VlAlert,
  VlTitle,
  VlSelect,
  VlInputField,
  VlInputAddon,
  VlInputGroup,
  VlRadio,
  VlCheckbox,
  VlDatepicker,
  VlDocument,
  VlModal,
  VlModalToggle,
  VlFormMessageLabel,
  VlFormMessageError,
  VlFormMessageAnnotation,
  VlUpload,
  VlFormValidation,
  VlFormValidationObserver,
  extend,
  VlTypography
} from '@govflanders/vl-ui-vue-components';

import * as Sentry from '@sentry/vue';

import DwseProcedure from '@dwse/procedure';
import DwseAutoComplete from '@dwse/autocomplete';
import DwseDatepicker from '@dwse/datepicker';

// // INSTALL SENTRY ERROR TRACKING // //
Sentry.init({
  Vue,
  dsn: 'https://579d2abd09f940f6a998be41da7d9e83@o290548.ingest.sentry.io/1871046',
  integrations: [
    new Sentry.BrowserTracing(),
    new Sentry.Replay()
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.05,

  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [/^https:\/\/api\.wse\.vlaanderen\.be.*/],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0
});

Vue.prototype.$sentry = Sentry;

// // INTERNATIONALISATION // //
const i18n = new VueI18n({
  locale: 'nl',
  fallbackLocale: 'nl'
});

// // INSTALL DEPENDENCIES // //
Vue.use(VueCustomElement);
Vue.use(VlUiVueComponents, {
  validation: {
    inject: false
  },
  components: {
    VlLink,
    VlButton,
    VlIcon,
    VlIconWrapper,
    VlAlert,
    VlTitle,
    VlSelect,
    VlInputField,
    VlInputAddon,
    VlInputGroup,
    VlRadio,
    VlModal,
    VlCheckbox,
    VlDatepicker,
    VlDocument,
    VlFormMessageLabel,
    VlFormMessageError,
    VlFormMessageAnnotation,
    VlUpload,
    VlFormValidation,
    VlFormValidationObserver,
    VlTypography
  }
});
Vue.directive('vl-modal-toggle', VlModalToggle);

extend('internationalPhone', internationalPhone);
extend('kbonummer', kboNummer);
extend('telnr', validateInternationalPhoneNumber);

Vue.use(DwseProcedure);
Vue.use(DwseAutoComplete);
Vue.use(DwseDatepicker);

// // PROVIDE ENVIRONMENT VARIABLES // //
window.__DWSE_GENERIC_FORMS__ = {
  SPM_BASE_URL: process.env.VUE_APP_SPM_BASE_URL,
  TP_BASE_URL: process.env.VUE_APP_TP_BASE_URL
};

// // INSTALL COMPONENTS // //

const registerComponent = (key, component) => {
  component.i18n = i18n;

  Vue.customElement(key, component);
};

switch (process.env.VUE_APP_FORMS_IMPORT_MODE) {
  case 'latest':
    import('@pkg/abk-simulator')
      .then(({ DwseAbkSimulator }) => registerComponent('wse-abk-simulator', DwseAbkSimulator));
    import('@pkg/abk-beroepskaarten-simulator')
      .then(({ DwseAbkBeroepskaartenSimulator }) => registerComponent('wse-abk-beroepskaarten-simulator', DwseAbkBeroepskaartenSimulator));
    import('@pkg/ova-simulator')
      .then(({ DwseOvaSimulator }) => registerComponent('wse-ova-simulator', DwseOvaSimulator));
    import('@pkg/spm-registratie')
      .then(({ DwseSpmRegistratie }) => registerComponent('wse-spm-registratie', DwseSpmRegistratie));
    import('@pkg/tp-aanvraag')
      .then(({ DwseTpAanvraag }) => registerComponent('wse-tp-aanvraag', DwseTpAanvraag));
    import('@pkg/vza-simulator')
      .then(({ DwseVzaSimulator }) => registerComponent('wse-vza-simulator', DwseVzaSimulator));
    import('@pkg/ssb-stagebonus-simulator')
      .then(({ DwseSsbStagebonusSimulator }) => registerComponent('wse-ssb-stagebonus-simulator', DwseSsbStagebonusSimulator));
    import('@pkg/ssb-startbonus-simulator')
      .then(({ DwseSsbStartbonusSimulator }) => registerComponent('wse-ssb-startbonus-simulator', DwseSsbStartbonusSimulator));
    import('@pkg/awap-simulator')
      .then(({ DwseAwapSimulator }) => registerComponent('wse-awap-simulator', DwseAwapSimulator));
    import('@pkg/llr-simulator')
      .then(({ DwseLLRSimulator }) => registerComponent('wse-llr-simulator', DwseLLRSimulator));
    import('@pkg/jb-simulator')
      .then(({ DwseJBSimulator }) => registerComponent('wse-jb-simulator', DwseJBSimulator));
    import('@pkg/jb-plus-simulator')
      .then(({ DwseJBPlusSimulator }) => registerComponent('wse-jb-plus-simulator', DwseJBPlusSimulator));
    import('@pkg/opch-simulator')
      .then(({ DwseOpchSimulator }) => registerComponent('wse-opch-simulator', DwseOpchSimulator));
    import('@pkg/imw-simulator')
      .then(({ DwseIMWSimulator }) => registerComponent('wse-imw-simulator', DwseIMWSimulator));
    break;

  case 'versioned':
  default:
    import('@dwse-forms/abk-simulator')
      .then(({ DwseAbkSimulator }) => registerComponent('wse-abk-simulator', DwseAbkSimulator));
    import('@dwse-forms/abk-beroepskaarten-simulator')
      .then(({ DwseAbkBeroepskaartenSimulator }) => registerComponent('wse-abk-beroepskaarten-simulator', DwseAbkBeroepskaartenSimulator));
    import('@dwse-forms/ova-simulator')
      .then(({ DwseOvaSimulator }) => registerComponent('wse-ova-simulator', DwseOvaSimulator));
    import('@dwse-forms/spm-registratie')
      .then(({ DwseSpmRegistratie }) => registerComponent('wse-spm-registratie', DwseSpmRegistratie));
    import('@dwse-forms/tp-aanvraag')
      .then(({ DwseTpAanvraag }) => registerComponent('wse-tp-aanvraag', DwseTpAanvraag));
    import('@dwse-forms/vza-simulator')
      .then(({ DwseVzaSimulator }) => registerComponent('wse-vza-simulator', DwseVzaSimulator));
    import('@dwse-forms/ssb-stagebonus-simulator')
      .then(({ DwseSsbStagebonusSimulator }) => registerComponent('wse-ssb-stagebonus-simulator', DwseSsbStagebonusSimulator));
    import('@dwse-forms/ssb-startbonus-simulator')
      .then(({ DwseSsbStartbonusSimulator }) => registerComponent('wse-ssb-startbonus-simulator', DwseSsbStartbonusSimulator));
    import('@dwse-forms/awap-simulator')
      .then(({ DwseAwapSimulator }) => registerComponent('wse-awap-simulator', DwseAwapSimulator));
    import('@dwse-forms/llr-simulator')
      .then(({ DwseLLRSimulator }) => registerComponent('wse-llr-simulator', DwseLLRSimulator));
    import('@dwse-forms/jb-simulator')
      .then(({ DwseJBSimulator }) => registerComponent('wse-jb-simulator', DwseJBSimulator));
    import('@dwse-forms/jb-plus-simulator')
      .then(({ DwseJBPlusSimulator }) => registerComponent('wse-jb-plus-simulator', DwseJBPlusSimulator));
    import('@dwse-forms/opch-simulator')
      .then(({ DwseOpchSimulator }) => registerComponent('wse-opch-simulator', DwseOpchSimulator));
    import('@dwse-forms/imw-simulator')
      .then(({ DwseIMWSimulator }) => registerComponent('wse-imw-simulator', DwseIMWSimulator));
}
